import { useTranslation } from "react-i18next";
import { X, Info } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function TraceabilityDrawer({ open, data, onClose }) {
  const { t } = useTranslation();
  if (!open) return null;

  return (
    <div
      data-testid="traceability-drawer"
      className="fixed inset-y-0 right-0 w-full sm:w-[500px] bg-white border-l hairline shadow-2xl z-50 flex flex-col animate-fade-up"
    >
      <div className="flex items-center justify-between h-14 px-5 border-b hairline">
        <div className="flex items-center gap-2">
          <Info size={16} className="text-salnes-emerald" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-salnes-stone">MRV</div>
            <div className="font-serif text-lg">{t("fase3.traceability_title")}</div>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} data-testid="traceability-close">
          <X size={18} />
        </Button>
      </div>
      <div className="p-5 overflow-y-auto space-y-4 flex-1 text-sm">
        <Section title={t("fase3.formula")}>
          <code className="block bg-salnes-sand/40 rounded px-3 py-2 text-xs">
            {data?.formula || "kgCO₂e = km × consumo_medio_l/km × factor_kgco2e/l"}
          </code>
        </Section>
        {data?.factor && (
          <Section title={t("fase3.factor")}>
            <div className="rounded-lg border hairline p-3 space-y-1">
              <Row label={t("fase3.category")} value={data.factor.category} />
              <Row label={t("fase3.scope")} value={data.factor.scope} />
              <Row label={t("fase3.value")} value={`${data.factor.value_kgco2e_per_unit} kgCO₂e / ${data.factor.unit_input}`} />
              <Row label={t("fase3.factor_source")} value={data.factor.source_name} />
              {data.factor.source_url && (
                <a
                  href={data.factor.source_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-salnes-atlantic underline break-all"
                  data-testid="traceability-source-link"
                >
                  {data.factor.source_url}
                </a>
              )}
              <Row label={t("fase3.factor_version")} value={`v${data.factor.version}`} />
              <Row label={t("fase3.factor_valid_from")} value={data.factor.valid_from} />
            </div>
          </Section>
        )}
        {data?.inputs && (
          <Section title="Inputs">
            <pre className="text-[11px] bg-salnes-ivory rounded px-3 py-2 overflow-x-auto">
              {JSON.stringify(data.inputs, null, 2)}
            </pre>
          </Section>
        )}
        {data?.computed_at && (
          <div className="text-[11px] uppercase tracking-[0.16em] text-salnes-stone">
            {t("fase3.computed_at")}: {data.computed_at}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-salnes-stone mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 text-sm">
      <span className="text-salnes-stone text-[11px] uppercase tracking-[0.16em]">{label}</span>
      <span className="text-right text-salnes-ink">{value ?? "—"}</span>
    </div>
  );
}
